export type BlockType = 
  | "MESSAGE"
  | "MULTIPLE_CHOICE"
  | "SHORT_ANSWER"
  | "CHECKBOX" // Legacy support
  | "CTA"
  | "IMAGE"
  | "RECOMMENDATION"
  | "EMAIL"
  | "PHONE"

// Rich text content type for Tiptap
export interface RichTextContent {
  type: 'rich' | 'plain'  // Backward compatibility
  plain?: string          // Fallback plain text
  json?: any              // Tiptap JSONContent
}

// Helper type: supports both old string and new rich text
export type TextContent = string | RichTextContent

export interface MessageBlockContent {
  text: TextContent
  typingAnimation: boolean
  autoAdvance?: boolean        // Auto-advance to next step
  autoAdvanceDelay?: number    // Delay in ms before auto-advance (default: 2000)
  image?: BlockImage
}

export interface MultipleChoiceBlockContent {
  question: TextContent
  subtitle?: TextContent
  options: TextContent[]
  allowMultiple: boolean
  image?: BlockImage
}

export interface ShortAnswerBlockContent {
  question: TextContent
  placeholder: string
  image?: BlockImage
}

export interface EmailBlockContent {
  question: TextContent
  placeholder: string
  image?: BlockImage
}

export interface PhoneBlockContent {
  question: TextContent
  placeholder: string
  image?: BlockImage
}


export interface CTAButton {
  id: string
  buttonText: string
  mode: 'externalUrl' | 'nextStep'
  url?: string // For externalUrl mode
}

export interface CTABlockContent {
  text: TextContent
  
  // NEW: Multiple buttons support
  buttons?: CTAButton[]
  
  // LEGACY: Single button fields (kept for backward compatibility)
  buttonText?: string
  buttonUrl?: string
  mode?: 'externalUrl' | 'nextStep'
  url?: string
  handle?: string // Legacy support for shopifyProduct mode
  
  image?: BlockImage
}

export interface ImageBlockContent {
  src: string
  alt: string
  caption?: string
}

export interface ProductInfo {
  title: string
  description?: string
  price?: string
  imageUrl?: string
  productUrl?: string
  handle?: string // For backwards compatibility
}

export interface RecommendationBlockContent {
  // PRIMARY: Default products (always available - main feature)
  products?: ProductInfo[] | string[] // Products to recommend by default
  
  // OPTIONAL: Answer-based mapping (advanced feature)
  enableMapping?: boolean // Toggle for answer-based recommendations
  questionId?: string // Which question to base recommendations on (optional)
  productMap?: Record<string, ProductInfo[] | string[]> // Maps answers to products (optional)
  
  // Customization
  headingText?: TextContent // Customizable heading text with rich text support
  shopDomain?: string // Shopify store domain for fetching products
  
  // LEGACY: Keep for backwards compatibility
  fallbackHandles?: string[] // Old fallback format
  fallbackProducts?: ProductInfo[] // Old fallback format
}

export type BlockContent = 
  | MessageBlockContent
  | MultipleChoiceBlockContent
  | ShortAnswerBlockContent
  | CTABlockContent
  | ImageBlockContent
  | RecommendationBlockContent
  | EmailBlockContent
  | PhoneBlockContent

export interface Block {
  id: string
  type: BlockType
  content: BlockContent
  order: number
  funnelId: string
  createdAt: Date
  updatedAt: Date
}

export interface Funnel {
  id: string
  title: string
  description?: string
  userId: string
  createdAt: Date
  updatedAt: Date
  blocks: Block[]
}

export interface User {
  id: string
  name?: string
  email: string
  emailVerified?: Date
  image?: string
  createdAt: Date
  updatedAt: Date
}

// Theme system types
export interface ThemeColors {
  primary: string // Button color, progress bar color
  text: string // Main text color
  background: string // Background color
  secondary?: string // Secondary button/hover states
  buttonText: string // Button text color
}

export interface ThemeFonts {
  heading: string // Font family for headings
  body: string // Font family for body text
}

// Image positioning for block images
export interface BlockImage {
  src: string
  caption?: string
  position: 'top' | 'center' | 'bottom'
  fadeEdge?: boolean // Fade left edge where it borders content (desktop only)
}

export interface Theme {
  colors: ThemeColors
  fonts: ThemeFonts
  showProgressIndicator?: boolean
}

// Default theme
export const defaultTheme: Theme = {
  colors: {
    primary: '#4F46E5', // indigo-600
    text: '#111827', // gray-900
    background: '#FFFFFF', // white
    secondary: '#6366F1', // indigo-500
    buttonText: '#FFFFFF', // white
  },
  fonts: {
    heading: 'Inter', // Default font family
    body: 'Inter',
  },
  showProgressIndicator: false
}

// Utility functions for TextContent
export function isRichText(content: TextContent): content is RichTextContent {
  return typeof content === 'object' && content !== null && 'type' in content
}

export function getPlainText(content: TextContent): string {
  if (typeof content === 'string') return content
  return content.plain || ''
}

export function getRichTextJson(content: TextContent): any {
  if (typeof content === 'string') {
    // Convert plain string to Tiptap JSON format
    return {
      type: 'doc',
      content: [{
        type: 'paragraph',
        content: content ? [{
          type: 'text',
          text: content
        }] : []
      }]
    }
  }
  return content.json || {
    type: 'doc',
    content: [{
      type: 'paragraph',
      content: []
    }]
  }
}

export function createRichTextContent(json: any, plain?: string): RichTextContent {
  return {
    type: 'rich',
    json,
    plain: plain || extractPlainTextFromJson(json)
  }
}

// Extract plain text from Tiptap JSON for fallback
function extractPlainTextFromJson(json: any): string {
  if (!json || !json.content) return ''
  
  return json.content
    .map((node: any) => {
      if (node.type === 'paragraph' && node.content) {
        return node.content
          .filter((n: any) => n.type === 'text')
          .map((n: any) => n.text)
          .join('')
      }
      return ''
    })
    .join('\n')
}
